import { IconForCheckbox, Text, TextSize, Weight } from 'components'

import React, { createRef, useEffect, useState } from 'react'

import { StateConfig } from 'components/charts/config'
import styled from 'styled-components'
import { GLOBAL_COLORS } from 'theme'
import { ChartIntervalTypes } from 'pages/index-details/storage/interface'

interface TitleProps {
  active: boolean
  width?: string
  borderStyle?: boolean
  borderRadiusStyle?: 'all' | 'left' | 'right' | 'none'
}
interface SelectProps {
  list: StateConfig[]
  onChange: (value: React.SetStateAction<ChartIntervalTypes>) => void
  className?: string
  isCheckIcon?: boolean
  width?: string
  borderStyle?: boolean
  borderRadiusStyle?: 'all' | 'left' | 'right' | 'none'
}

const SelectWrapper = styled.div<TitleProps>`
  position: relative;
  width: ${(props) => (props.width ? props.width : '100%')};
  transition: width 0.4s ease-out -0.2s;
  display: flex;
  background-color: ${GLOBAL_COLORS.transparent};
  width: ${(props) => (props.width ? props.width : '100%')};
  border-radius: 10px 10px 0px 0px;
  height: 42px;
  width: 140px;
  box-sizing: border-box;
  border: ${({ active, theme }) => active && `1px solid ${theme.borderColor}`};
`

const HeaderTitle = styled.div<{ active: boolean }>`
  display: flex;
  justify-content: flex-start;
  max-width: 90px;
  padding-left: 6px;
  span {
    color: ${({ active }) => active && `${GLOBAL_COLORS.coralBlue} !important`};
  }
`

const StyledTitle = styled.button<TitleProps>`
  width: 100%;
  display: flex;
  align-items: center;
  border: 0px;
  color: ${({ theme }) => theme.primaryFontColor};
  background-color: ${({ theme }) => theme.bg};
  justify-content: space-between;
  cursor: pointer;
  border-radius: 10px;
  & span {
    color: ${({ theme }) => theme.primaryFontColor};
    font-weight: ${Weight.SemiBold};
  }
`

const ScrollList = styled.div<React.HTMLProps<HTMLDivElement>>`
  position: absolute;
  left: -1px;
  top: 100%;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-top: none;
  z-index: 1;
  max-height: 145px;
  overflow-y: auto;
  border-radius: 0px 0px 10px 10px;
  width: 140px;
  box-sizing: border-box;
`

const ListItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.bg};
  border: none;
  overflow: hidden;
  padding-left: 12px;
  width: 100%;
  height: 42px;
  cursor: pointer;
  transition: 0.2s ease-out;
  &:hover {
    background-color: ${({ theme }) => theme.activeItemColor};
  }
`

const ItemContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledText = styled(Text)`
  font-size: ${TextSize.small};
  padding: 3px 16px 0px 8px;
  color: ${({ theme }) => theme.secondaryFontColor};
  font-weight: ${Weight.SemiBold};
`

interface ArrowProps {
  size: number
  direction: 'top' | 'right' | 'bottom' | 'left'
}

const WidgetArrow = styled.span<ArrowProps>`
  display: flex;
  height: ${(props) => props.size + 'px'};
  width: ${(props) => props.size + 'px'};
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  border-right: 1px solid ${({ theme }) => theme.borderColor};
  transition: 0.2s ease-out;
  margin-left: 7px;
  ${(props) => {
    switch (props.direction) {
      case 'top':
        return `
          margin-top: 3px;
          transform: rotate(225deg);
        `
      case 'right':
        return `
          transform: rotate(315deg);
          margin-right: 3px;
        `
      case 'bottom':
        return `
          transform: rotate(45deg);
          margin-bottom: 3px;
        `
      case 'left':
        return `
          transform: rotate(135deg);
          margin-left: 3px;
        `
    }
  }}
`

export const CommonSelect: React.FC<SelectProps> = ({
  className,
  list,
  onChange,
  isCheckIcon,
  width,
  borderStyle,
  borderRadiusStyle = 'all',
}) => {
  const [isListOpen, setListOpen] = useState(false)
  const preselectedRange = list[2] //1 year

  const [selectedItem, setSelectedItem] = useState<StateConfig>(preselectedRange)

  const toggleList = () => {
    setListOpen(!isListOpen)
  }

  const handlerSelectItem = (item: StateConfig) => {
    setSelectedItem(item)
    setListOpen(!isListOpen)
    onChange(item.name)
  }

  const selectRef = createRef<HTMLDivElement>()

  const closeOnOutOfBoundsBlur = ({ relatedTarget }: React.FocusEvent<Element>) => {
    if (relatedTarget === null) {
      setListOpen(false)
    }
  }

  useEffect(() => {
    const handleClickOutside: EventListener = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setListOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [selectRef])

  return (
    <SelectWrapper
      ref={selectRef}
      className={className}
      active={isListOpen}
      width={width}
      borderStyle={borderStyle}
      borderRadiusStyle={borderRadiusStyle}
    >
      <StyledTitle onBlur={closeOnOutOfBoundsBlur} onClick={toggleList} active={isListOpen}>
        <HeaderTitle active={isListOpen}>
          <StyledText type={'p'}>{selectedItem.name}</StyledText>
        </HeaderTitle>
        {list.length > 1 && <WidgetArrow size={5} direction={isListOpen ? 'top' : 'bottom'} />}
      </StyledTitle>
      {isListOpen && (
        <ScrollList>
          {list
            .filter((el) => el.name !== selectedItem.name)
            .map((item) => (
              <ListItem key={item.range} onClick={() => handlerSelectItem(item)}>
                <ItemContent>
                  <StyledText type={'p'}>{item.name}</StyledText>
                </ItemContent>
                {isCheckIcon && item.name === selectedItem.name && <IconForCheckbox />}
              </ListItem>
            ))}
        </ScrollList>
      )}
    </SelectWrapper>
  )
}
